<template>
  <div class="inspection-wrapper flex-container">
    <v-alert
      v-if="unresolvedIndustryTargets.length"
      type="warning"
      class="industry-warning"
      text
    >
      <div>
        <strong>Annahme noch nicht möglich</strong>
        <div class="mt-1">
          Für die folgenden Angaben muss vor der Annahme eine gültige
          SAP-Branche ausgewählt werden:
          {{ unresolvedIndustryLabels.join(', ') }}.
        </div>
        <div class="industry-warning-actions mt-2">
          <v-btn
            v-for="target in unresolvedIndustryTargets"
            :key="target.rowKey"
            small
            outlined
            color="primary"
            @click="$emit('edit-industry', target.rowKey)"
          >
            {{ target.label }} bearbeiten
          </v-btn>
        </div>
      </div>
    </v-alert>
    <div class="checkbox-groups">
      <div
        v-for="group in inspectionGroups"
        :key="group.key"
        class="checkbox-group"
      >
        <p class="group-title">{{ group.title }}</p>
        <div
          v-for="item in group.items"
          :key="item.key"
          class="inspection-item-field"
        >
          <div
            class="inspection-item inspection-item--clickable"
            :class="{
              'inspection-item--error':
                inspectionValidationAttempted &&
                checkedItems[item.key] !== true,
            }"
            @click="toggleItem(item.key)"
          >
            <v-checkbox
              :input-value="checkedItems[item.key]"
              :label="item.title"
              color="#9B004B"
              class="inspection-checkbox mt-0 pt-0"
              hide-details
              @click.stop
              @change="setItem(item.key, $event)"
            />
          </div>
          <div
            v-if="
              inspectionValidationAttempted && checkedItems[item.key] !== true
            "
            class="inspection-item-error"
          >
            Bitte bestätigen.
          </div>
        </div>
      </div>

      <div
        v-if="hasSupportedInspectionFields"
        class="checkbox-group checkbox-group--sap"
      >
        <p class="group-title">SAP</p>
        <div class="sap-fields">
          <v-select
            v-if="isNewConstruction"
            v-model="installationReason"
            :items="installationReasonOptions"
            :loading="installationReasonLoading"
            :error-messages="installationReasonErrors"
            label="Einbaugrund *"
            outlined
            @blur="installationReasonTouched = true"
          />
          <v-select
            v-if="isNewConstruction"
            v-model="customerType"
            :items="customerTypeOptions"
            :loading="customerTypeLoading"
            :error-messages="customerTypeErrors"
            label="Kundenart *"
            outlined
            @blur="customerTypeTouched = true"
            @change="onCustomerTypeChange"
          />
          <v-select
            v-if="isNewConstruction && customerType === 'Gruppe'"
            v-model="partnerGroupType"
            :items="partnerGroupTypeOptions"
            :loading="partnerGroupTypeLoading"
            :error-messages="partnerGroupTypeErrors"
            label="Partnergruppenart *"
            outlined
            @blur="partnerGroupTypeTouched = true"
          />
          <v-select
            v-if="isNewConstruction"
            v-model="businessPartnerType"
            :items="businessPartnerTypeOptions"
            :loading="businessPartnerTypeLoading"
            :error-messages="businessPartnerTypeErrors"
            label="Geschäftspartnerart *"
            outlined
            @blur="businessPartnerTypeTouched = true"
          />
          <v-select
            v-if="isNewConstruction"
            v-model="specialMeter"
            :items="specialMeterOptions"
            :loading="specialMeterLoading"
            :error-messages="specialMeterErrors"
            label="Sonderzähler *"
            outlined
            @blur="specialMeterTouched = true"
          />
          <v-select
            v-if="isNewConstruction && isGas"
            v-model="pressureLevel"
            :items="pressureLevelOptions"
            :loading="pressureLevelLoading"
            :error-messages="pressureLevelErrors"
            label="Druckstufe *"
            outlined
            @blur="pressureLevelTouched = true"
          />
          <v-select
            v-if="isNewConstruction && isElectricity"
            v-model="voltageLevel"
            :items="voltageLevelOptions"
            :loading="voltageLevelLoading"
            :error-messages="voltageLevelErrors"
            label="Spannungsebene *"
            outlined
            @blur="voltageLevelTouched = true"
          />
          <v-select
            v-if="isNewConstruction && isElectricityOrGas"
            v-model="countingMethod"
            :items="countingMethodOptions"
            :loading="countingMethodLoading"
            :error-messages="countingMethodErrors"
            label="Zählverfahren *"
            outlined
            @blur="countingMethodTouched = true"
          />
          <v-text-field
            v-if="isNewConstruction && isElectricity"
            v-model="electricityGridConnectionCapacity"
            :error-messages="electricityGridConnectionCapacityErrors"
            label="Stromnetzanschlusskapazität (kW) *"
            type="number"
            inputmode="decimal"
            min="0"
            max="9999999"
            step="any"
            outlined
            @blur="electricityGridConnectionCapacityTouched = true"
          />
          <v-text-field
            v-if="isNewConstruction && isGas"
            v-model="gasGridConnectionCapacity"
            :error-messages="gasGridConnectionCapacityErrors"
            label="Gasnetzanschlusskapazität (kW) *"
            type="number"
            inputmode="decimal"
            min="0"
            max="9999999"
            step="any"
            outlined
            @blur="gasGridConnectionCapacityTouched = true"
          />
          <v-select
            v-if="isExistingOrRemoval"
            v-model="measureReason"
            :items="measureReasonOptions"
            :loading="measureReasonLoading"
            :error-messages="measureReasonErrors"
            label="Sachverhalt (Ausbau-/Wechselgrund) *"
            outlined
            @blur="measureReasonTouched = true"
          />
          <v-select
            v-if="usesIndustryField"
            v-model="industryCode"
            :items="industryOptions"
            :loading="industryLoading"
            :error-messages="industryErrors"
            label="Branche *"
            outlined
            @blur="industryTouched = true"
            @change="onIndustryChange"
          />
          <v-select
            v-model="messageType"
            :items="messageTypeOptions"
            :loading="messageTypeLoading"
            :error-messages="messageTypeErrors"
            label="Meldungsart *"
            outlined
            @blur="messageTypeTouched = true"
          />
          <v-select
            v-if="isGasRemoval"
            v-model="isLastGasMeter"
            :items="yesNoOptions"
            :error-messages="lastGasMeterErrors"
            label="Letzter Gaszähler *"
            outlined
            @blur="lastGasMeterTouched = true"
          />
          <v-text-field
            v-if="isNewConstruction && isElectricityOrGas"
            v-model="jvp"
            :error-messages="jvpErrors"
            label="JVP (kWh/Jahr) *"
            type="number"
            inputmode="decimal"
            min="0"
            max="9999999"
            step="any"
            outlined
            @blur="jvpTouched = true"
          />
        </div>
        <p v-if="isGas" class="regionetz-info">
          Zähler im Bereich Regionetz:
          <strong>
            {{
              regionetzAreaLoading
                ? 'Wird ermittelt …'
                : regionetzAreaDisplayValue
            }}
          </strong>
        </p>
      </div>
      <v-textarea
        v-if="supportsAddition"
        v-model="addition"
        class="mx-4"
        label="Ergänzung"
        rows="1"
        outlined
      />
    </div>

    <div class="flex-container-nested border-radius-bottom">
      <div class="buttons-label">
        <div v-if="showLoading" class="d-flex align-center justify-center">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mr-4"
          ></v-progress-circular>
        </div>
        <div v-else>
          <button
            type="button"
            class="btn-confirm-inspection"
            :disabled="showLoading"
            @click="confirmInspection"
          >
            <div class="btn-text" style="width: inherit; color: white">
              Prüfung bestätigen
            </div>
          </button>
          <button
            type="button"
            class="btn-cancel-inspection"
            @click="declineInspection"
          >
            <div class="btn-text" style="width: inherit">Prüfung ablehnen</div>
          </button>
          <button
            type="button"
            class="btn-save-current-state"
            @click="updateInspectionValues()"
          >
            Zwischenstand speichern
          </button>
          <v-alert
            class="alert_success"
            style="background-color: #e4f0d6 !important"
            :value="alert"
            max-width="270px"
            max-height="35px"
            text
            transition="scale-transition"
            v-if="
              !alertError &&
              lastUpdatedComponent === 'commissioningSaveCurrentResult'
            "
          >
            <template v-slot:append>
              <v-icon color="green" class="ml-2">mdi-check</v-icon>
            </template>
            Erfolgreich gespeichert
          </v-alert>
          <v-alert
            type="error"
            class="alert_error"
            :value="alert"
            max-width="270px"
            max-height="35px"
            v-if="
              alertError &&
              lastUpdatedComponent === 'commissioningSaveCurrentResult'
            "
            text
            transition="scale-transition"
          >
            Speicherung fehlgeschlagen
          </v-alert>
        </div>
      </div>
    </div>

    <NewModalDialog
      v-if="isModalVisible"
      :value="noteValue"
      role="dialog"
      @update-note-data="noteValue.note = $event"
      @cancel-data="closeModal"
      @save-data="closeModalAndSaveData"
    >
      <template v-slot:body>
        <div class="dialogTitle">Prüfung ablehnen</div>
      </template>
      <template v-slot:footer>
        <div class="footer_container">
          <div v-if="showDeclineError" class="error_message_dialog">
            Bitte Ablehnungsgrund eingeben.
          </div>
          <div class="button_container">
            <button
              type="button"
              class="btn-save"
              @click="closeModalAndSaveData"
              :disabled="showDeclineLoading"
            >
              <v-progress-circular
                indeterminate
                color="white"
                class="mr-4"
                v-if="showDeclineLoading"
              ></v-progress-circular>
              Prüfung ablehnen
            </button>
            <button type="button" class="btn-cancel" @click="closeModal">
              abbrechen
            </button>
            <v-alert
              type="error"
              class="alert_error mt-4"
              max-width="270px"
              v-if="
                lastUpdatedComponent === 'commissioningOrderRefused' &&
                alertError
              "
              text
              transition="scale-transition"
            >
              Ablehnen fehlgeschlagen
            </v-alert>
          </div>
        </div>
      </template>
    </NewModalDialog>
  </div>
</template>

<script>
import { NewModalDialog } from '@newnetzportal/netzportal-webapp-storybook-components';
import noteService from '@/services/NoteService';
import {
  getBusinessValues,
  updateCommissioningOrderStatus,
  updateCommissioningTechnicalInspectionData,
} from '@/services/CommissioningService';
import { emptyEventData } from '@/store/ordersModule';
import {
  isCommissioningCustomerIndustryRequired,
  isUnresolvedCommissioningBusinessType,
} from '@/helpers/commissioningBusinessType';
import { hasCommissioningSystemChangeDetails } from '@/helpers/commissioningSystemChange';

function cloneInspectionData(inspectionData) {
  return JSON.parse(JSON.stringify(inspectionData || {}));
}

function toNullableNumber(value) {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number(normalizedValue.replace(',', '.'));
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function validatePositiveNumber(value) {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return 'Dieses Feld ist erforderlich.';
  }

  const parsedValue = Number(normalizedValue.replace(',', '.'));
  if (!Number.isFinite(parsedValue)) {
    return 'Bitte geben Sie eine Zahl ein.';
  }
  if (parsedValue <= 0) {
    return 'Der Wert muss größer als 0 sein.';
  }
  if (parsedValue > 9999999) {
    return 'Der Wert darf 9.999.999 nicht überschreiten.';
  }
  return null;
}

const BUSINESS_VALUE_ENERGY_TAGS = {
  ELECTRIC_METER_ORDER: 'Electricity',
  GAS_METER_ORDER: 'Gas',
  SITE_ELECTRIC_METER_ORDER: 'Electricity',
  WATER_METER_ORDER: 'Water',
};

export default {
  name: 'CommissioningInspectionComponent',
  components: {
    NewModalDialog,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      checkedItems: {
        flow_rate_or_units_valid: false,
        meter_size_matching: false,
        counter_number_matches_photo: false,
      },
      inspectionValidationAttempted: false,
      addition: '',
      installationReason: null,
      installationReasonTouched: false,
      installationReasonOptions: [],
      installationReasonLoading: false,
      customerType: null,
      customerTypeTouched: false,
      customerTypeOptions: [],
      customerTypeLoading: false,
      partnerGroupType: null,
      partnerGroupTypeTouched: false,
      partnerGroupTypeOptions: [],
      partnerGroupTypeLoading: false,
      businessPartnerType: null,
      businessPartnerTypeTouched: false,
      businessPartnerTypeOptions: [],
      businessPartnerTypeLoading: false,
      specialMeter: null,
      specialMeterTouched: false,
      specialMeterOptions: [],
      specialMeterLoading: false,
      pressureLevel: null,
      pressureLevelTouched: false,
      pressureLevelOptions: [],
      pressureLevelLoading: false,
      voltageLevel: null,
      voltageLevelTouched: false,
      voltageLevelOptions: [],
      voltageLevelLoading: false,
      countingMethod: null,
      countingMethodTouched: false,
      countingMethodOptions: [],
      countingMethodLoading: false,
      electricityGridConnectionCapacity: null,
      electricityGridConnectionCapacityTouched: false,
      gasGridConnectionCapacity: null,
      gasGridConnectionCapacityTouched: false,
      jvp: null,
      jvpTouched: false,
      messageType: null,
      messageTypeTouched: false,
      messageTypeOptions: [],
      messageTypeLoading: false,
      measureReason: null,
      measureReasonTouched: false,
      measureReasonOptions: [],
      measureReasonLoading: false,
      industryCode: null,
      industryText: null,
      industryTouched: false,
      industryOptions: [],
      industryLoading: false,
      isLastGasMeter: null,
      lastGasMeterTouched: false,
      isMeterInRegionetzArea: null,
      regionetzAreaLoading: false,
      oldInspectionData: {},
      showLoading: false,
      alert: false,
      alertError: false,
      isModalVisible: false,
      noteValue: { note: '' },
      showDeclineError: false,
      showDeclineLoading: false,
    };
  },
  computed: {
    payload() {
      return this.order?.payload || {};
    },
    userId() {
      return this.$store.getters.getUserId;
    },
    userDisplayName() {
      const firstName = this.$store.getters.getUserFirstName || '';
      const lastName = this.$store.getters.getUserLastName || '';
      return `${firstName} ${lastName}`.trim() || this.userId;
    },
    lastUpdatedComponent() {
      return this.$store.getters.getLastUpdatedComponent || '';
    },
    notifyEvent() {
      return this.$store.getters.getNotifyEvent || emptyEventData;
    },
    isNewConstruction() {
      return this.payload.order_type === 'zaehlereinbau';
    },
    isEnergyInstallation() {
      return (
        this.isNewConstruction &&
        ['ELECTRIC_METER_ORDER', 'GAS_METER_ORDER'].includes(
          this.payload.journey_type
        )
      );
    },
    syncsApplicationReason() {
      return (
        this.isExistingOrRemoval &&
        [
          'ELECTRIC_METER_ORDER',
          'GAS_METER_ORDER',
          'WATER_METER_ORDER',
        ].includes(this.payload.journey_type)
      );
    },
    isElectricity() {
      return ['ELECTRIC_METER_ORDER', 'SITE_ELECTRIC_METER_ORDER'].includes(
        this.payload.journey_type
      );
    },
    isGas() {
      return this.payload.journey_type === 'GAS_METER_ORDER';
    },
    isWater() {
      return this.payload.journey_type === 'WATER_METER_ORDER';
    },
    isWaterInstallation() {
      return this.isWater && this.isNewConstruction;
    },
    isElectricityOrGas() {
      return this.isElectricity || this.isGas;
    },
    isExistingOrRemoval() {
      return (
        this.payload.order_type === 'zaehlerausbau' ||
        this.payload.order_type === 'zaehlerwechsel'
      );
    },
    isGasRemoval() {
      return this.isGas && this.payload.order_type === 'zaehlerausbau';
    },
    // Zählerwechsel WAÄ Gas/Wasser calculates a meter size, so it is checked while Sachverhalt stays WAÄ (#25137).
    checksSystemChangeMeterSize() {
      return (
        (this.isGas || this.isWater) &&
        hasCommissioningSystemChangeDetails(this.payload) &&
        this.measureReason === 'WAÄ'
      );
    },
    hasSupportedInspectionFields() {
      return this.isNewConstruction || this.isExistingOrRemoval;
    },
    usesIndustryField() {
      return (
        this.syncsApplicationIndustry ||
        (this.isExistingOrRemoval && !this.syncsApplicationReason)
      );
    },
    syncsApplicationIndustry() {
      return this.isWaterInstallation || this.isEnergyInstallation;
    },
    installationReasonErrors() {
      return this.installationReasonTouched && !this.installationReason
        ? ['Bitte Einbaugrund auswählen.']
        : [];
    },
    customerTypeErrors() {
      return this.customerTypeTouched && !this.customerType
        ? ['Bitte Kundenart auswählen.']
        : [];
    },
    partnerGroupTypeErrors() {
      return this.customerType === 'Gruppe' &&
        this.partnerGroupTypeTouched &&
        !this.partnerGroupType
        ? ['Bitte Partnergruppenart auswählen.']
        : [];
    },
    businessPartnerTypeErrors() {
      return this.businessPartnerTypeTouched && !this.businessPartnerType
        ? ['Bitte Geschäftspartnerart auswählen.']
        : [];
    },
    specialMeterErrors() {
      return this.specialMeterTouched && this.specialMeter === null
        ? ['Bitte Sonderzähler auswählen.']
        : [];
    },
    pressureLevelErrors() {
      return this.pressureLevelTouched && !this.pressureLevel
        ? ['Bitte Druckstufe auswählen.']
        : [];
    },
    voltageLevelErrors() {
      return this.voltageLevelTouched && !this.voltageLevel
        ? ['Bitte Spannungsebene auswählen.']
        : [];
    },
    countingMethodErrors() {
      return this.countingMethodTouched && !this.countingMethod
        ? ['Bitte Zählverfahren auswählen.']
        : [];
    },
    electricityGridConnectionCapacityErrors() {
      if (!this.electricityGridConnectionCapacityTouched) {
        return [];
      }
      const error = validatePositiveNumber(
        this.electricityGridConnectionCapacity
      );
      return error ? [error] : [];
    },
    gasGridConnectionCapacityErrors() {
      if (!this.gasGridConnectionCapacityTouched) {
        return [];
      }
      const error = validatePositiveNumber(this.gasGridConnectionCapacity);
      return error ? [error] : [];
    },
    jvpErrors() {
      if (!this.jvpTouched) {
        return [];
      }
      const error = validatePositiveNumber(this.jvp);
      return error ? [error] : [];
    },
    messageTypeErrors() {
      return this.messageTypeTouched && !this.messageType
        ? ['Bitte Meldungsart auswählen.']
        : [];
    },
    measureReasonErrors() {
      return this.measureReasonTouched && !this.measureReason
        ? ['Bitte Sachverhalt auswählen.']
        : [];
    },
    industryErrors() {
      return this.industryTouched && !this.industryCode
        ? ['Bitte Branche auswählen.']
        : [];
    },
    lastGasMeterErrors() {
      return this.lastGasMeterTouched &&
        typeof this.isLastGasMeter !== 'boolean'
        ? ['Bitte Ja oder Nein auswählen.']
        : [];
    },
    businessValueEnergyTag() {
      return BUSINESS_VALUE_ENERGY_TAGS[this.payload.journey_type];
    },
    regionetzAreaDisplayValue() {
      if (this.isMeterInRegionetzArea === null) {
        return '';
      }
      return this.isMeterInRegionetzArea ? 'Ja' : 'Nein';
    },
    yesNoOptions() {
      return [
        { text: 'Ja', value: true },
        { text: 'Nein', value: false },
      ];
    },
    inspectionGroups() {
      const groups = [];

      if (this.isWater && this.isNewConstruction) {
        groups.push({
          key: 'new-construction',
          title: 'Neubau',
          items: [
            {
              key: 'flow_rate_or_units_valid',
              title: 'Durchflussmenge / Wohneinheiten valide',
            },
            {
              key: 'meter_size_matching',
              title: 'Zählergröße passend',
            },
          ],
        });
      }

      if (this.isExistingOrRemoval) {
        groups.push({
          key: 'existing-removal',
          title: 'Ausbau / Wechsel',
          items: [
            {
              key: 'counter_number_matches_photo',
              title: 'Zählernummer passend zu Foto',
            },
            ...(this.checksSystemChangeMeterSize
              ? [{ key: 'meter_size_matching', title: 'Zählergröße passend' }]
              : []),
          ],
        });
      }

      return groups;
    },
    visibleInspectionKeys() {
      return this.inspectionGroups.reduce((keys, group) => {
        group.items.forEach((item) => keys.push(item.key));
        return keys;
      }, []);
    },
    supportsAddition() {
      return (
        this.hasSupportedInspectionFields &&
        [
          'WATER_METER_ORDER',
          'GAS_METER_ORDER',
          'ELECTRIC_METER_ORDER',
        ].includes(this.payload.journey_type)
      );
    },
    canConfirmInspection() {
      return (
        this.unresolvedIndustryTargets.length === 0 &&
        this.visibleInspectionKeys.every(
          (key) => this.checkedItems[key] === true
        ) &&
        (!this.isNewConstruction ||
          (Boolean(this.installationReason) &&
            Boolean(this.customerType) &&
            Boolean(this.businessPartnerType) &&
            this.specialMeter !== null &&
            (!this.isGas || Boolean(this.pressureLevel)) &&
            (!this.isElectricity || Boolean(this.voltageLevel)) &&
            (!this.isElectricityOrGas || Boolean(this.countingMethod)) &&
            (!this.isElectricity ||
              !validatePositiveNumber(
                this.electricityGridConnectionCapacity
              )) &&
            (!this.isGas ||
              !validatePositiveNumber(this.gasGridConnectionCapacity)) &&
            (!this.isElectricityOrGas || !validatePositiveNumber(this.jvp)) &&
            (!this.isGas || typeof this.isMeterInRegionetzArea === 'boolean') &&
            Boolean(this.messageType) &&
            (!this.usesIndustryField || Boolean(this.industryCode)) &&
            (this.customerType !== 'Gruppe' ||
              Boolean(this.partnerGroupType)))) &&
        (!this.isExistingOrRemoval ||
          (Boolean(this.measureReason) &&
            (!this.usesIndustryField || Boolean(this.industryCode)) &&
            Boolean(this.messageType) &&
            (!this.isGasRemoval || typeof this.isLastGasMeter === 'boolean') &&
            (!this.isGas || typeof this.isMeterInRegionetzArea === 'boolean')))
      );
    },
    unresolvedIndustryTargets() {
      if (!this.isNewConstruction) {
        return [];
      }

      const targets = [];
      const contactPerson = this.payload.contact_person || {};
      const customerIndustryRequired =
        isCommissioningCustomerIndustryRequired(contactPerson);

      if (isUnresolvedCommissioningBusinessType(this.payload.business_type)) {
        targets.push({
          rowKey: 'business-type',
          label: 'Branche des Anschlussobjekts',
        });
      }

      if (
        (customerIndustryRequired &&
          !String(contactPerson.business_type?.industry_code || '').trim()) ||
        isUnresolvedCommissioningBusinessType(contactPerson.business_type)
      ) {
        targets.push({
          rowKey: 'customer-business-type',
          label: 'Branche des Kunden',
        });
      }

      return targets;
    },
    unresolvedIndustryLabels() {
      return this.unresolvedIndustryTargets.map((target) => target.label);
    },
  },
  created() {
    this.initializeInspectionValues();
    Promise.all([
      this.loadInstallationReasons(),
      this.loadCustomerTypes(),
      this.loadPartnerGroupTypes(),
      this.loadBusinessPartnerTypes(),
      this.loadSpecialMeters(),
      this.loadPressureLevels(),
      this.loadVoltageLevels(),
      this.loadCountingMethods(),
      this.loadMeasureReasons(),
      this.loadIndustries(),
      this.loadMessageTypes(),
      this.loadRegionetzArea(),
    ]);
  },
  methods: {
    async loadInstallationReasons() {
      if (!this.isNewConstruction || !this.businessValueEnergyTag) {
        return;
      }

      this.installationReasonLoading = true;
      const values = await getBusinessValues(
        'Einbaugrund',
        this.businessValueEnergyTag
      );
      this.installationReasonOptions = Array.isArray(values)
        ? values.map((value) => ({
            text: `${value.Grund} (${value.Bezeichnung})`,
            value: value.Bezeichnung,
          }))
        : [];
      this.installationReasonLoading = false;
    },
    async loadCustomerTypes() {
      if (!this.isNewConstruction || !this.businessValueEnergyTag) {
        return;
      }

      this.customerTypeLoading = true;
      const values = await getBusinessValues(
        'Kundenart',
        this.businessValueEnergyTag
      );
      this.customerTypeOptions = Array.isArray(values)
        ? values.map((value) => value.Bezeichnung)
        : [];
      this.customerTypeLoading = false;
    },
    async loadPartnerGroupTypes() {
      if (!this.isNewConstruction || !this.businessValueEnergyTag) {
        return;
      }

      this.partnerGroupTypeLoading = true;
      const values = await getBusinessValues(
        'Partnergruppenart',
        this.businessValueEnergyTag
      );
      this.partnerGroupTypeOptions = Array.isArray(values)
        ? values.map((value) => ({
            text: `${value.Bezeichnung} (${value.Kürzel})`,
            value: value.Bezeichnung,
          }))
        : [];
      this.partnerGroupTypeLoading = false;
    },
    async loadBusinessPartnerTypes() {
      if (!this.isNewConstruction || !this.businessValueEnergyTag) {
        return;
      }

      this.businessPartnerTypeLoading = true;
      const values = await getBusinessValues(
        'Geschäftspartnerart',
        this.businessValueEnergyTag
      );
      this.businessPartnerTypeOptions = Array.isArray(values)
        ? values.map((value) => value.Bezeichnung)
        : [];
      this.businessPartnerTypeLoading = false;
    },
    async loadSpecialMeters() {
      if (!this.isNewConstruction || !this.businessValueEnergyTag) {
        return;
      }

      this.specialMeterLoading = true;
      const values = await getBusinessValues(
        'Sonderzähler',
        this.businessValueEnergyTag
      );
      this.specialMeterOptions = Array.isArray(values)
        ? values
            .sort((a, b) => a.Kürzel.localeCompare(b.Kürzel))
            .map((value) => ({
              text: value.Bezeichnung,
              value: value.Bezeichnung,
            }))
        : [];
      this.specialMeterLoading = false;
    },
    async loadCountingMethods() {
      if (!this.isNewConstruction || !this.isElectricityOrGas) {
        return;
      }

      this.countingMethodLoading = true;
      const values = await getBusinessValues(
        'Zählverfahren',
        this.businessValueEnergyTag
      );
      this.countingMethodOptions = Array.isArray(values)
        ? values
            .sort((a, b) => a.Kürzel.localeCompare(b.Kürzel))
            .map((value) => ({
              text: `${value.Bezeichnung} (${value.Kürzel})`,
              value: value.Bezeichnung,
            }))
        : [];
      this.countingMethodLoading = false;
    },
    async loadPressureLevels() {
      if (!this.isNewConstruction || !this.isGas) {
        return;
      }

      this.pressureLevelLoading = true;
      const values = await getBusinessValues(
        'Druckstufe',
        this.businessValueEnergyTag
      );
      this.pressureLevelOptions = Array.isArray(values)
        ? values
            .sort((a, b) => b.Kürzel.localeCompare(a.Kürzel))
            .map((value) => ({
              text: `${value.Bezeichnung} (${value.Kürzel})`,
              value: value.Bezeichnung,
            }))
        : [];
      this.pressureLevelLoading = false;
    },
    async loadVoltageLevels() {
      if (!this.isNewConstruction || !this.isElectricity) {
        return;
      }

      this.voltageLevelLoading = true;
      const values = await getBusinessValues(
        'Spannungsebene',
        this.businessValueEnergyTag
      );
      this.voltageLevelOptions = Array.isArray(values)
        ? values
            .sort((a, b) => a.Kürzel.localeCompare(b.Kürzel))
            .map((value) => ({
              text: `${value.Beschreibung} (${value.Kürzel})`,
              value: value.Beschreibung,
            }))
        : [];
      this.voltageLevelLoading = false;
    },
    async loadMessageTypes() {
      const city = this.payload.connection_address?.city;
      if (
        !this.hasSupportedInspectionFields ||
        !city ||
        !this.businessValueEnergyTag
      ) {
        return;
      }

      this.messageTypeLoading = true;
      const values = await getBusinessValues(
        'Meldungsart',
        `${city},${this.businessValueEnergyTag}`
      );
      this.messageTypeOptions = Array.isArray(values)
        ? values.map((value) => ({
            text: `${value.Meldung} (${value.Art})`,
            value: value.Art,
          }))
        : [];
      if (this.messageType === null && this.messageTypeOptions.length === 1) {
        this.messageType = this.messageTypeOptions[0].value;
      }
      this.messageTypeLoading = false;
    },
    async loadMeasureReasons() {
      if (!this.isExistingOrRemoval || !this.businessValueEnergyTag) {
        return;
      }

      this.measureReasonLoading = true;
      const values = await getBusinessValues(
        'WechselAusbaugrund',
        this.businessValueEnergyTag
      );
      const category = this.payload.order_type === 'zaehlerausbau' ? 'a' : 'w';
      this.measureReasonOptions = Array.isArray(values)
        ? values
            .filter((value) => value['Ausbau/Wechsel'] === category)
            .sort((a, b) => a.Grund.localeCompare(b.Grund, 'de'))
            .map((value) => ({
              text: `${value.Grund} (${value.Bezeichnung})`,
              value: value.Bezeichnung,
              applicationValue: {
                business_value_id: value.id,
                code: value.Bezeichnung,
                label: value.Grund,
                category: value['Ausbau/Wechsel'],
              },
            }))
        : [];
      this.measureReasonLoading = false;
    },
    async loadIndustries() {
      if (!this.usesIndustryField) {
        return;
      }

      this.industryLoading = true;
      const values = await getBusinessValues('Branche', 'Public');
      this.industryOptions = Array.isArray(values)
        ? values
            .sort((a, b) => a.Beschreibung.localeCompare(b.Beschreibung, 'de'))
            .map((value) => ({
              text: `${value.Beschreibung} (${value.Branche})`,
              value: String(value.Branche),
              industryText: value.Beschreibung,
            }))
        : [];
      this.onIndustryChange(this.industryCode);
      this.industryLoading = false;
    },
    async loadRegionetzArea() {
      if (!this.hasSupportedInspectionFields || !this.isGas) {
        return;
      }

      const zip = String(
        this.payload.connection_address?.zip ||
          this.payload.connection_address_locator?.zip ||
          ''
      ).trim();
      if (!zip) {
        return;
      }

      this.regionetzAreaLoading = true;
      const values = await getBusinessValues(
        'NetzbetreiberPLZ',
        `${zip},Gas,Regionetz`
      );
      if (Array.isArray(values)) {
        this.isMeterInRegionetzArea = values.length > 0;
      }
      this.regionetzAreaLoading = false;
    },
    buildInspectionData(source = this.checkedItems) {
      const inspectionData = this.visibleInspectionKeys.reduce((data, key) => {
        data[key] = source[key] === true;
        return data;
      }, {});

      if (this.isNewConstruction) {
        inspectionData.installation_reason = this.installationReason;
        inspectionData.customer_type = this.customerType;
        inspectionData.business_partner_type = this.businessPartnerType;
        inspectionData.special_meter = this.specialMeter;
        if (this.isElectricityOrGas) {
          inspectionData.counting_method = this.countingMethod;
          inspectionData.jvp = toNullableNumber(this.jvp);
        }
        if (this.isElectricity) {
          inspectionData.voltage_level = this.voltageLevel;
          inspectionData.electricity_grid_connection_capacity =
            toNullableNumber(this.electricityGridConnectionCapacity);
        }
        if (this.isGas) {
          inspectionData.pressure_level = this.pressureLevel;
          inspectionData.gas_grid_connection_capacity = toNullableNumber(
            this.gasGridConnectionCapacity
          );
        }
        if (this.customerType === 'Gruppe') {
          inspectionData.partner_group_type = this.partnerGroupType;
        }
      }

      if (this.hasSupportedInspectionFields) {
        inspectionData.message_type = this.messageType;
        if (this.isGas) {
          inspectionData.is_meter_in_regionetz_area =
            this.isMeterInRegionetzArea;
        }
      }

      if (this.isExistingOrRemoval) {
        inspectionData.measure_reason = this.measureReason;
        if (this.isGas) {
          inspectionData.is_last_gas_meter = this.isLastGasMeter;
        }
      }

      if (this.usesIndustryField) {
        inspectionData.industry_code = this.industryCode;
        inspectionData.industry_text = this.industryText;
      }

      if (this.supportsAddition) {
        inspectionData.addition = this.addition;
      }

      return inspectionData;
    },
    initializeInspectionValues() {
      this.inspectionValidationAttempted = false;
      const savedInspectionData = cloneInspectionData(
        this.payload?.technical_inspection_data
      );
      if (this.isEnergyInstallation) {
        if (savedInspectionData.jvp === undefined) {
          savedInspectionData.jvp =
            this.payload.expected_annual_consumption_kwh;
        }
        if (
          this.isElectricity &&
          savedInspectionData.electricity_grid_connection_capacity === undefined
        ) {
          savedInspectionData.electricity_grid_connection_capacity =
            this.payload.simultaneously_required_kw;
        }
        if (
          this.isGas &&
          savedInspectionData.gas_grid_connection_capacity === undefined
        ) {
          savedInspectionData.gas_grid_connection_capacity =
            this.payload.total_nominal_load_kw;
        }
      }

      if (this.syncsApplicationReason) {
        if (savedInspectionData.measure_reason === undefined) {
          savedInspectionData.measure_reason =
            this.payload.measure_reason?.code;
        }
      }

      if (this.syncsApplicationIndustry) {
        if (savedInspectionData.industry_code === undefined) {
          savedInspectionData.industry_code =
            this.payload.business_type?.industry_code;
          savedInspectionData.industry_text =
            this.payload.business_type?.industry_text;
        }
      }

      this.checkedItems = {
        flow_rate_or_units_valid:
          savedInspectionData.flow_rate_or_units_valid === true,
        meter_size_matching: savedInspectionData.meter_size_matching === true,
        counter_number_matches_photo:
          savedInspectionData.counter_number_matches_photo === true,
      };
      this.addition = savedInspectionData.addition ?? '';
      this.installationReason = savedInspectionData.installation_reason || null;
      this.installationReasonTouched = false;
      this.customerType = savedInspectionData.customer_type || null;
      this.customerTypeTouched = false;
      this.partnerGroupType = savedInspectionData.partner_group_type || null;
      this.partnerGroupTypeTouched = false;
      this.businessPartnerType =
        savedInspectionData.business_partner_type || null;
      this.businessPartnerTypeTouched = false;
      this.specialMeter =
        savedInspectionData.special_meter === undefined
          ? null
          : savedInspectionData.special_meter;
      this.specialMeterTouched = false;
      this.pressureLevel = savedInspectionData.pressure_level || null;
      this.pressureLevelTouched = false;
      this.voltageLevel = savedInspectionData.voltage_level || null;
      this.voltageLevelTouched = false;
      this.countingMethod = savedInspectionData.counting_method || null;
      this.countingMethodTouched = false;
      this.electricityGridConnectionCapacity =
        savedInspectionData.electricity_grid_connection_capacity ?? null;
      this.electricityGridConnectionCapacityTouched = false;
      this.gasGridConnectionCapacity =
        savedInspectionData.gas_grid_connection_capacity ?? null;
      this.gasGridConnectionCapacityTouched = false;
      this.jvp = savedInspectionData.jvp ?? null;
      this.jvpTouched = false;
      this.messageType = savedInspectionData.message_type || null;
      this.messageTypeTouched = false;
      this.measureReason = savedInspectionData.measure_reason || null;
      this.measureReasonTouched = false;
      this.industryCode = this.usesIndustryField
        ? savedInspectionData.industry_code || null
        : null;
      this.industryText = this.usesIndustryField
        ? savedInspectionData.industry_text || null
        : null;
      this.industryTouched = false;
      this.isLastGasMeter =
        typeof savedInspectionData.is_last_gas_meter === 'boolean'
          ? savedInspectionData.is_last_gas_meter
          : null;
      this.lastGasMeterTouched = false;
      this.isMeterInRegionetzArea =
        typeof savedInspectionData.is_meter_in_regionetz_area === 'boolean'
          ? savedInspectionData.is_meter_in_regionetz_area
          : null;
      this.oldInspectionData = this.buildInspectionData(savedInspectionData);
    },
    onCustomerTypeChange(value) {
      if (value !== 'Gruppe') {
        this.partnerGroupType = null;
        this.partnerGroupTypeTouched = false;
      }
    },
    onIndustryChange(value) {
      const selectedIndustry = this.industryOptions.find(
        (option) => option.value === value
      );
      if (selectedIndustry) {
        this.industryText = selectedIndustry.industryText;
      } else if (!value) {
        this.industryText = null;
      }
    },
    setItem(key, value) {
      this.$set(this.checkedItems, key, value);
    },
    toggleItem(key) {
      this.$set(this.checkedItems, key, !this.checkedItems[key]);
    },
    confirmInspection() {
      this.inspectionValidationAttempted = true;

      if (this.isNewConstruction) {
        this.installationReasonTouched = true;
        this.customerTypeTouched = true;
        this.partnerGroupTypeTouched = this.customerType === 'Gruppe';
        this.businessPartnerTypeTouched = true;
        this.specialMeterTouched = true;
        this.pressureLevelTouched = this.isGas;
        this.voltageLevelTouched = this.isElectricity;
        this.countingMethodTouched = this.isElectricityOrGas;
        this.electricityGridConnectionCapacityTouched = this.isElectricity;
        this.gasGridConnectionCapacityTouched = this.isGas;
        this.messageTypeTouched = true;
        this.jvpTouched = this.isElectricityOrGas;
        this.industryTouched = this.usesIndustryField;
      }

      if (this.isExistingOrRemoval) {
        this.measureReasonTouched = true;
        this.industryTouched = this.usesIndustryField;
        this.messageTypeTouched = true;
        this.lastGasMeterTouched = this.isGasRemoval;
      }

      if (!this.canConfirmInspection) {
        return;
      }
      this.showLoading = true;
      this.updateInspectionValues('completed');
    },
    declineInspection() {
      this.isModalVisible = true;
      this.showDeclineError = false;
    },
    closeModal() {
      this.isModalVisible = false;
      this.noteValue = { note: '' };
      this.showDeclineError = false;
    },
    async closeModalAndSaveData() {
      if (this.noteValue.note === '') {
        this.showDeclineError = true;
        return;
      }

      this.showDeclineLoading = true;

      try {
        await noteService.addCommissioningNote(
          {
            id: this.order.id,
            user_id: this.order.user_id,
            title: 'Ablehnungsgrund',
            note: this.noteValue.note,
          },
          this.userId
        );
        this.$store.commit(
          'setLastUpdatedComponent',
          'commissioningOrderRefused'
        );
        if (
          this.isEnergyInstallation ||
          this.syncsApplicationReason ||
          (this.isWater && this.isNewConstruction)
        ) {
          if (await this.updateInspectionValues('declined')) {
            this.closeModal();
          }
          return;
        }
        await updateCommissioningOrderStatus(
          {
            id: this.order.id,
            old_value: {
              status: this.order.status,
            },
            new_value: {
              status: 'ORDER_DECLINED',
            },
            remark: this.noteValue.note,
          },
          this.userId
        );
        this.$emit('update-order', {
          status: 'ORDER_DECLINED',
        });
        this.closeModal();
      } catch (error) {
        this.alertError = true;
        console.log(error);
      } finally {
        this.showDeclineLoading = false;
      }
    },
    async updateInspectionValues(status) {
      this.alert = false;
      this.alertError = false;

      const currentInspectionData = this.buildInspectionData();
      const changedObject = {
        id: this.order.id,
        new_value: {
          technical_inspection_data: currentInspectionData,
        },
      };

      if (this.syncsApplicationReason) {
        const selectedReason = this.measureReasonOptions.find(
          (option) => option.value === this.measureReason
        );
        const applicationReason = this.payload.measure_reason;
        const measureReason =
          selectedReason?.applicationValue ||
          (applicationReason?.code === this.measureReason
            ? applicationReason
            : null);
        if (this.measureReason && !measureReason) {
          this.showLoading = false;
          this.alertError = true;
          return false;
        }
        changedObject.new_value.measure_reason = this.measureReason
          ? measureReason
          : null;
      }

      if (Object.keys(this.oldInspectionData).length > 0) {
        changedObject.old_value = {
          technical_inspection_data: this.oldInspectionData,
        };
      }

      if (status) {
        changedObject.status = status;
      }
      if (status === 'declined') {
        changedObject.remark = this.noteValue.note;
      }

      this.$store.commit(
        'setLastUpdatedComponent',
        status === 'declined'
          ? 'commissioningOrderRefused'
          : 'commissioningSaveCurrentResult'
      );
      return updateCommissioningTechnicalInspectionData(
        changedObject,
        this.userId
      )
        .then((res) => {
          this.showLoading = false;
          if (!res || res.error) {
            this.alert = false;
            this.alertError = true;
            return false;
          } else {
            this.oldInspectionData = currentInspectionData;
            const updatedOrder = {
              technical_inspection_data: currentInspectionData,
            };

            if (this.isEnergyInstallation) {
              updatedOrder.payload = {
                expected_annual_consumption_kwh: currentInspectionData.jvp,
                ...(this.isGas
                  ? {
                      total_nominal_load_kw:
                        currentInspectionData.gas_grid_connection_capacity,
                    }
                  : {
                      simultaneously_required_kw:
                        currentInspectionData.electricity_grid_connection_capacity,
                    }),
              };
            }

            if (this.syncsApplicationReason) {
              updatedOrder.payload = {
                measure_reason: changedObject.new_value.measure_reason,
                ...(currentInspectionData.measure_reason
                  ? { measure_reason_unavailable: false }
                  : {}),
              };
            }

            if (this.syncsApplicationIndustry) {
              updatedOrder.payload = {
                ...updatedOrder.payload,
                business_type: currentInspectionData.industry_code
                  ? {
                      ...this.payload.business_type,
                      industry_code: currentInspectionData.industry_code,
                      industry_text: currentInspectionData.industry_text,
                      industry_not_found: false,
                    }
                  : null,
              };
            }

            if (status === 'completed') {
              updatedOrder.status = 'ORDER_ACCEPTED';
            } else if (status === 'declined') {
              updatedOrder.status = 'ORDER_DECLINED';
            }

            this.$emit('update-order', updatedOrder);
            return true;
          }
        })
        .catch((error) => {
          this.showLoading = false;
          this.alert = false;
          this.alertError = true;
          console.log(error);
          return false;
        });
    },
  },
  watch: {
    notifyEvent(newValue) {
      if (newValue.field === 'updateCommissioningTechnicalInspectionData') {
        this.alert = true;
      }
      setTimeout(() => {
        this.alert = false;
        this.$store.commit('setNotifyEvent', emptyEventData);
        this.$store.commit('setLastUpdatedComponent', '');
      }, 3000);
    },
    'order.id': {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          return;
        }

        this.initializeInspectionValues();
      },
    },
  },
};
</script>

<style scoped>
.inspection-wrapper {
  display: flex;
  flex-direction: column;
  color: #6e6e6e;
}

.industry-warning {
  margin-bottom: 16px;
  background-color: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-left: 5px solid #d6a400 !important;
  border-radius: 8px;
  box-shadow: 0 1px 2px #00000029;
  color: #6e6e6e !important;
}

.industry-warning::before {
  opacity: 0 !important;
}

.industry-warning strong {
  color: #6e6e6e;
  font-size: 20px;
}

.industry-warning-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.checkbox-groups {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px 8px 0 0;
}

.checkbox-group {
  width: 100%;
  max-width: 470px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.checkbox-group--sap {
  max-width: none;
}

.sap-fields {
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  column-gap: 16px;
}

.group-title {
  font-weight: bold;
  margin-bottom: 12px;
  font-size: 20px;
}

.inspection-item {
  padding: 0 16px;
  min-height: 56px;
  display: flex;
  align-items: center;
  background: #ffffff;
  box-shadow: 0px 1px 2px #00000029;
  border-radius: 4px;
  border: thin solid rgba(0, 0, 0, 0.12);
}

.inspection-item-field {
  margin-bottom: 12px;
}

.inspection-item--error {
  border-color: #ff5252;
}

.inspection-item-error {
  margin: 4px 12px 0;
  color: #ff5252;
  font-size: 12px;
}

.inspection-item--clickable {
  cursor: pointer;
}

.inspection-checkbox {
  width: 100%;
}

.inspection-checkbox ::v-deep .v-input__slot {
  margin-bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
}

.inspection-checkbox ::v-deep .v-label {
  color: #6e6e6e;
  opacity: 1;
  font-size: 20px;
  line-height: 24px;
  margin-right: auto;
}

.flex-container {
  display: flex;
  color: #6e6e6e;
}

.flex-container-nested {
  display: flex;
  background-color: rgba(255, 255, 255, 0.9);
  min-height: 56px;
  flex-direction: row;
  padding: 16px;
  justify-content: space-between;
  margin-bottom: 1px;
}

.buttons-label {
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  display: flex;
}

.border-radius-bottom {
  border-radius: 0px 0px 8px 8px;
}

.btn-cancel-inspection {
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 2px 1px #00000029;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  height: 40px;
  width: 252px;
  border: none;
  opacity: 1;
  margin-left: 30px;
}

.btn-confirm-inspection {
  background: #9b004b 0% 0% no-repeat padding-box;
  box-shadow: 0px 2px 1px #00000029;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  height: 40px;
  width: 252px;
  border: none;
  opacity: 1;
}

.btn-confirm-inspection:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-save-current-state {
  border: none;
  font-size: 18px;
  cursor: pointer;
  background: transparent;
  text-decoration: underline;
  color: #6e6e6e !important;
  margin-left: 30px;
}

@media screen and (max-width: 1279px) and (min-width: 1024px) {
  .flex-container-nested {
    display: flex;
    flex-direction: column;
    background-color: rgba(255, 255, 255, 0.9);
    min-height: 56px;
    padding: 16px;
    justify-content: space-between;
    margin-bottom: 1px;
  }
}

@media screen and (max-width: 959px) {
  .checkbox-group {
    max-width: none;
  }

  .sap-fields {
    grid-template-rows: none;
    grid-auto-flow: row;
    grid-template-columns: minmax(0, 1fr);
  }

  .buttons-label {
    flex-wrap: wrap;
    gap: 12px;
  }

  .btn-cancel-inspection,
  .btn-save-current-state {
    margin-left: 0;
  }
}

.alert_success,
.alert_error {
  display: inline-flex !important;
  margin-top: 0 !important;
  margin-left: 16px;
  align-items: center;
  height: 35px !important;
}
</style>
